import { HHAlert } from '@hinge-health/react-component-library/dist/components/atoms/hh-alert';
import { HHStack } from '@hinge-health/react-component-library/dist/components/atoms/hh-stack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClientInsurerBatchList } from '../components/client-insurers/client-insurer-batch-list';
import { ContractPackageFormValues } from '../components/contract/package/custom-package-types';
import { ContractPackageForm } from '../components/contract/package/form';
import LoadingComponent from '../components/utils/loading';
import { routes } from '../constants/strings/routes';
import {
  BatchClientInsurerType,
  useCreateBatchContractMutation,
  useGetClientInsurersByIdsQuery,
} from '../types';
export const CLIENT_INSURER_BATCH_CREATE_CONTRACT_CONTAINER_TEST_ID =
  'client-insurer-batch-create-contract-container';

const ClientInsurerBatchContractCreate = (): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlIds = searchParams.get('ids');
  const initialIds = urlIds?.split(',').map(id => parseInt(id)) ?? [];

  const [dataInitialized, setDataInitialized] = useState(false);
  const [clientInsurersList, setClientInsurersList] = useState<
    Set<BatchClientInsurerType>
  >(new Set());

  const isUserAction = useRef(false);

  const { data, error, loading } = useGetClientInsurersByIdsQuery({
    variables: {
      ids: initialIds,
    },
  });

  const [createBatchContractMutation, { data: createData }] =
    useCreateBatchContractMutation();

  useEffect(() => {
    if (data && !dataInitialized && searchParams.get('ids')) {
      setClientInsurersList(new Set(data?.getClientInsurersByIds || []));
      setDataInitialized(true);
    }
  }, [data, dataInitialized, searchParams]);

  useEffect(() => {
    if (!dataInitialized) return;

    if (isUserAction.current) {
      const ids = Array.from(clientInsurersList)
        .map(ci => ci.id)
        .join(',');
      if (ids) {
        setSearchParams({ ids }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
      isUserAction.current = false;
    }
  }, [clientInsurersList, dataInitialized, setSearchParams]);

  useEffect(() => {
    if (createData) {
      navigate(`../${routes.billing.batchContractCreateStatus}`, {
        state: {
          data: createData.createBatchContract,
          clientInsurersList: Array.from(clientInsurersList),
        },
      });
    }
  }, [createData, clientInsurersList, navigate]);

  const handleCancel = (): void => {
    navigate(`${routes.billing.main}`);
  };

  const batchSubmit = (values: ContractPackageFormValues): void => {
    const clientInsurersContractCreate = Array.from(clientInsurersList).map(
      ci => ({
        id: ci.id,
        insurerId: ci.insurer.id,
        clientId: ci.client.id,
      }),
    );

    const {
      startDate,
      endDate,
      contractPackages,
      partnershipId,
      userAnnualCap,
      globalDailyEngagementCap,
      currency,
      billingType,
    } = values;

    createBatchContractMutation({
      variables: {
        batchContractInput: {
          clientInsurers: clientInsurersContractCreate,
          endDate,
          startDate,
          contractPackages,
          partnershipId,
          userAnnualCap,
          globalDailyEngagementCap,
          currency,
          billingType,
        },
      },
    });
  };

  const handleRemoveClientInsurerFromList = useCallback((id: number): void => {
    isUserAction.current = true;
    setClientInsurersList(prev => {
      const newArray = Array.from(prev);
      const filteredArray = newArray.filter(ci => ci.id !== id);
      return new Set(filteredArray);
    });
  }, []);

  if (loading && !dataInitialized) {
    return <LoadingComponent />;
  }

  if (error) {
    return (
      <HHAlert severity="error" hhVariant="outlined">
        Error loading client insurers
      </HHAlert>
    );
  }

  if (clientInsurersList.size === 0) {
    return (
      <HHAlert severity="warning" hhVariant="outlined">
        No client insurers selected
      </HHAlert>
    );
  }

  return (
    <HHStack
      data-testid={CLIENT_INSURER_BATCH_CREATE_CONTRACT_CONTAINER_TEST_ID}
      direction="row"
      spacing={0}
      sx={{
        height: '100%',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <HHStack
        sx={{
          height: '100vh',
          overflowY: 'auto',
          flexShrink: 0,
          borderRight: '1px solid #e0e0e0',
        }}
      >
        <ClientInsurerBatchList
          clientInsurers={Array.from(clientInsurersList)}
          onRemove={handleRemoveClientInsurerFromList}
        />
      </HHStack>

      <HHStack
        direction="column"
        spacing={4}
        sx={{
          flex: '1 1 auto',
          padding: 4,
          overflowY: 'auto',
          height: '100vh',
        }}
      >
        <HHStack
          direction="column"
          spacing={4}
          sx={{
            width: '100%',
          }}
        >
          <ContractPackageForm
            onSubmitCallback={batchSubmit}
            onCancel={handleCancel}
            currentContracts={[]}
            disableSubmit={false}
            hideVoid
            submitTitle={'Create'}
            startDateDisablePast
            isBatchCreate
          />
        </HHStack>
      </HHStack>
    </HHStack>
  );
};
export default ClientInsurerBatchContractCreate;
